"use client";

/**
 * ErpUserMenu — full user meni dropdown za ERP topbar (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (lin 1001-1053).
 * Trigger: avatar u svetoj zoni desno.
 *
 * Sadržaj (po mockupu):
 *  - Header: avatar 48px + ime + email + role pill
 *  - Moj nalog (kbd: G P)
 *  - Moja podešavanja
 *  - API ključevi (badge: 2)
 *  - Sigurnost · 2FA (badge: Aktivno)
 *  - Aktivne sesije (badge: 3)
 *  - Promijeni temu (Light/Dark toggle)
 *  - Pomoć i prečice (kbd: ?)
 *  - Odjavi se (danger)
 *
 * Pravila:
 *  - PRAVILO 11: KLIK otvara, klik-van zatvara, ESC zatvara
 *  - PRAVILO 12: CSS vars only, dark mode kompatibilno
 */

import * as React from "react";
import Link from "next/link";
import {
  User,
  Settings,
  KeyRound,
  Shield,
  Clock,
  Sun,
  Info,
  LogOut,
} from "@grozd/icons";
import type { LucideIcon } from "@grozd/icons";
import { logout as authLogout } from "@grozd/auth-client";
import type { AuthUser } from "@grozd/auth-client";

interface ErpUserMenuProps {
  user?: AuthUser | null;
}

export function ErpUserMenu({ user }: ErpUserMenuProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const initials = React.useMemo(() => {
    const f = user?.first_name?.trim()?.[0] ?? "";
    const l = user?.last_name?.trim()?.[0] ?? "";
    if (f || l) return (f + l).toUpperCase();
    return user?.email?.[0]?.toUpperCase() ?? "?";
  }, [user]);

  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  const toggleTheme = React.useCallback(() => {
    const current = document.documentElement.getAttribute("data-theme");
    const next = current === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("grozd-theme", next);
    } catch {
      // ignore
    }
  }, []);

  async function handleLogout() {
    setOpen(false);
    await authLogout();
  }

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="font-brand"
        aria-label="Korisnički meni"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "linear-gradient(135deg, var(--primary), var(--deep))",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          border: "2px solid transparent",
          transition: "all 120ms cubic-bezier(0.22,1,0.36,1)",
          position: "relative",
        }}
      >
        {initials}
        <span
          aria-hidden
          style={{
            content: "''",
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "var(--success)",
            border: "2px solid var(--surface-card)",
          }}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Korisničke opcije"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            background: "var(--surface-card)",
            border: "1px solid var(--n100)",
            borderRadius: 14,
            boxShadow: "0 12px 40px rgba(45,27,105,0.18)",
            width: 280,
            zIndex: 80,
            overflow: "hidden",
            animation: "erpMenuIn 200ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              background: "linear-gradient(135deg, var(--p100), var(--tint))",
              borderBottom: "1px solid var(--n100)",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              className="font-brand"
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, var(--primary), var(--deep))",
                color: "#fff",
                fontWeight: 700,
                fontSize: 17,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
              }}
            >
              {initials}
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "var(--success)",
                  border: "2.5px solid var(--surface-card)",
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="font-brand"
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "var(--ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {fullName || "Korisnik"}
              </div>
              <div
                className="font-body"
                style={{
                  fontSize: 12,
                  color: "var(--n400)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: 2,
                }}
              >
                {user?.email ?? ""}
              </div>
              <span
                className="font-brand"
                style={{
                  marginTop: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  background: "linear-gradient(135deg,#FBBF24,#F97316)",
                  color: "#fff",
                }}
              >
                Tenant Admin
              </span>
            </div>
          </div>

          {/* Items */}
          <div style={{ padding: 6 }}>
            <UserMenuItem
              href="/podesavanja/profil"
              icon={User}
              label="Moj nalog"
              meta="G P"
              onClose={() => setOpen(false)}
            />
            <UserMenuItem
              href="/podesavanja"
              icon={Settings}
              label="Moja podešavanja"
              onClose={() => setOpen(false)}
            />
            <UserMenuItem
              href="/podesavanja/api-kljucevi"
              icon={KeyRound}
              label="API ključevi"
              metaPill={{ text: "2", tone: "neutral" }}
              onClose={() => setOpen(false)}
            />
            <UserMenuItem
              href="/podesavanja/sigurnost"
              icon={Shield}
              label="Sigurnost · 2FA"
              metaPill={{ text: "Aktivno", tone: "success" }}
              onClose={() => setOpen(false)}
            />
            <UserMenuItem
              href="/podesavanja/sesije"
              icon={Clock}
              label="Aktivne sesije"
              metaPill={{ text: "3", tone: "info" }}
              onClose={() => setOpen(false)}
            />

            <div
              style={{
                height: 1,
                background: "var(--n100)",
                margin: "4px 6px",
              }}
            />

            <button
              type="button"
              onClick={toggleTheme}
              className="erp-um__item"
              role="menuitem"
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "var(--ink)",
                cursor: "pointer",
                transition: "background 120ms",
                background: "transparent",
                border: "none",
                textAlign: "left",
              }}
            >
              <Sun size={16} strokeWidth={1.8} />
              <span>Promijeni temu</span>
              <span
                className="font-mono"
                style={{
                  marginLeft: "auto",
                  color: "var(--n400)",
                  fontSize: 10,
                }}
              >
                Light · Dark
              </span>
            </button>

            <UserMenuItem
              href="/pomoc"
              icon={Info}
              label="Pomoć i prečice"
              meta="?"
              onClose={() => setOpen(false)}
            />

            <div
              style={{
                height: 1,
                background: "var(--n100)",
                margin: "4px 6px",
              }}
            />

            <button
              type="button"
              onClick={handleLogout}
              className="erp-um__item erp-um__item--danger"
              role="menuitem"
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 13,
                color: "var(--danger)",
                cursor: "pointer",
                transition: "background 120ms",
                background: "transparent",
                border: "none",
                textAlign: "left",
              }}
            >
              <LogOut size={16} strokeWidth={1.8} />
              <span>Odjavi se</span>
            </button>
          </div>

          <style jsx>{`
            @keyframes erpMenuIn {
              from {
                opacity: 0;
                transform: translateY(-6px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            :global(.erp-um__item):hover {
              background: var(--tint) !important;
              color: var(--primary) !important;
            }
            :global(.erp-um__item--danger):hover {
              background: var(--danger-light) !important;
              color: var(--danger) !important;
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

interface UserMenuItemProps {
  href: string;
  icon: LucideIcon;
  label: string;
  meta?: string;
  metaPill?: { text: string; tone: "success" | "info" | "neutral" | "warning" };
  onClose: () => void;
}

function UserMenuItem({
  href,
  icon: Icon,
  label,
  meta,
  metaPill,
  onClose,
}: UserMenuItemProps) {
  const pillTone = metaPill
    ? {
        success: { bg: "var(--success-light)", color: "var(--success)" },
        info: { bg: "var(--info-light)", color: "var(--info)" },
        warning: { bg: "var(--warning-light)", color: "var(--warning)" },
        neutral: { bg: "var(--n100)", color: "var(--n600)" },
      }[metaPill.tone]
    : null;

  return (
    <Link
      href={href}
      onClick={onClose}
      className="erp-um__item"
      role="menuitem"
      style={{
        padding: "9px 12px",
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13,
        color: "var(--ink)",
        cursor: "pointer",
        transition: "background 120ms",
        textDecoration: "none",
      }}
    >
      <Icon size={16} strokeWidth={1.8} />
      <span>{label}</span>
      {meta && (
        <span
          className="font-mono"
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            padding: "2px 6px",
            borderRadius: 4,
            background: "var(--n50)",
            border: "1px solid var(--n100)",
            fontSize: 10.5,
            color: "var(--n600)",
            fontWeight: 600,
          }}
        >
          {meta}
        </span>
      )}
      {metaPill && pillTone && (
        <span
          className="font-brand"
          style={{
            marginLeft: "auto",
            fontSize: 9.5,
            fontWeight: 600,
            padding: "1px 6px",
            borderRadius: 999,
            background: pillTone.bg,
            color: pillTone.color,
          }}
        >
          {metaPill.text}
        </span>
      )}
    </Link>
  );
}

export default ErpUserMenu;
